import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BarChart2, Users, Briefcase, CheckCircle, Clock, TrendingUp } from "lucide-react";
import { recruitmentAPI } from "../services/api";

const STAGE_COLORS = {
  new: "#5e6ad2", screening: "#d97706", interview: "#7c3aed",
  offer: "#0284c7", hired: "#16a34a", rejected: "#dc2626",
};

const SOURCE_COLORS = {
  linkedin: "#0a66c2", indeed: "#2557a7", direct: "#5e6ad2",
  internal: "#7c3aed", referral: "#d97706",
};

const PIPELINE_STAGES = ["new", "screening", "interview", "offer", "hired"];

export default function RecrutementAnalytics() {
  const { t } = useTranslation();
  const stageLabel = (s) => t(`recruitment.analytics.stageLabels.${s}`, { defaultValue: s });
  const sourceLabel = (s) => t(`recruitment.analytics.sources.${s}`, { defaultValue: s });
  const { data: stats, isLoading } = useQuery({
    queryKey: ["recruitment-stats"],
    queryFn: () => recruitmentAPI.getStats().then((r) => r.data),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 rounded-xl animate-pulse" style={{ backgroundColor: "var(--lm-s2)" }} />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  // Build stage map for quick lookup
  const stageMap = Object.fromEntries(stats.applications_by_stage.map((s) => [s.stage, s.count]));
  // Funnel: only pipeline stages, not rejected
  const funnelStages = PIPELINE_STAGES.map((s) => ({ stage: s, count: stageMap[s] || 0 }));
  const funnelMax = Math.max(...funnelStages.map((f) => f.count), 1);

  // Source breakdown
  const totalBySource = stats.applications_by_source.reduce((a, b) => a + b.count, 0) || 1;

  const conversionRate = stats.total_applications > 0
    ? Math.round((stats.hired_count / stats.total_applications) * 100)
    : 0;

  return (
    <div className="space-y-8">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard icon={Briefcase} label={t("recruitment.analytics.kpi.postings")} value={stats.total_postings} color="#5e6ad2" />
        <KpiCard icon={Users} label={t("recruitment.analytics.kpi.candidates")} value={stats.total_candidates} color="#7c3aed" />
        <KpiCard icon={BarChart2} label={t("recruitment.analytics.kpi.applications")} value={stats.total_applications} color="#0284c7" />
        <KpiCard icon={CheckCircle} label={t("recruitment.analytics.kpi.hired")} value={stats.hired_count} color="#16a34a" />
        <KpiCard
          icon={Clock}
          label={t("recruitment.analytics.kpi.avgTime")}
          value={stats.avg_days_to_hire != null ? `${stats.avg_days_to_hire}${t("recruitment.analytics.dayShort")}` : "—"}
          color="#d97706"
        />
      </div>

      {/* Conversion rate banner */}
      <div
        className="rounded-xl px-6 py-4 flex items-center justify-between gap-4"
        style={{ backgroundColor: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.20)" }}
      >
        <div className="flex items-center gap-3">
          <TrendingUp className="h-5 w-5" style={{ color: "#16a34a" }} />
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>{t("recruitment.analytics.conversionTitle")}</p>
            <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
              {t("recruitment.analytics.conversionSub", { hired: stats.hired_count, total: stats.total_applications })}
            </p>
          </div>
        </div>
        <span className="text-3xl font-semibold" style={{ color: "#16a34a", letterSpacing: "-1px" }}>
          {conversionRate}%
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel */}
        <Section title={t("recruitment.analytics.funnelTitle")}>
          <div className="space-y-3">
            {funnelStages.map(({ stage, count }) => (
              <div key={stage} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: "var(--lm-ink-2)" }}>{stageLabel(stage)}</span>
                  <span className="font-medium" style={{ color: "var(--lm-ink)" }}>{count}</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--lm-s2)" }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.round((count / funnelMax) * 100)}%`,
                      backgroundColor: STAGE_COLORS[stage] || "#5e6ad2",
                      minWidth: count > 0 ? "4px" : "0",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          {/* Rejected note */}
          {(stageMap["rejected"] || 0) > 0 && (
            <p className="text-xs mt-3" style={{ color: "var(--lm-ink-3)" }}>
              {t("recruitment.analytics.rejectedNote", { count: stageMap["rejected"] })}
            </p>
          )}
        </Section>

        {/* Sources */}
        <Section title={t("recruitment.analytics.sourcesTitle")}>
          {stats.applications_by_source.length === 0 ? (
            <p className="text-xs py-6 text-center" style={{ color: "var(--lm-ink-3)" }}>{t("recruitment.analytics.noData")}</p>
          ) : (
            <div className="space-y-3">
              {stats.applications_by_source
                .sort((a, b) => b.count - a.count)
                .map(({ source, count }) => {
                  const pct = Math.round((count / totalBySource) * 100);
                  const color = SOURCE_COLORS[source] || "#5e6ad2";
                  return (
                    <div key={source} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span style={{ color: "var(--lm-ink-2)" }}>{sourceLabel(source)}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium" style={{ color: "var(--lm-ink)" }}>{count}</span>
                          <span style={{ color: "var(--lm-ink-3)" }}>{pct}%</span>
                        </div>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--lm-s2)" }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, backgroundColor: color, minWidth: count > 0 ? "4px" : "0" }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </Section>

        {/* Postings status */}
        <Section title={t("recruitment.analytics.postingsTitle")}>
          <div className="grid grid-cols-3 gap-3">
            {[
              { status: "draft", label: t("recruitment.analytics.postingStatus.draft"), color: "var(--lm-ink-3)" },
              { status: "published", label: t("recruitment.analytics.postingStatus.published"), color: "#16a34a" },
              { status: "closed", label: t("recruitment.analytics.postingStatus.closed"), color: "#dc2626" },
            ].map(({ status, label, color }) => {
              const count = stats.postings_by_status.find((p) => p.status === status)?.count || 0;
              return (
                <div
                  key={status}
                  className="rounded-xl p-4 text-center"
                  style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
                >
                  <p className="text-2xl font-semibold" style={{ color, letterSpacing: "-0.5px" }}>{count}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--lm-ink-3)" }}>{label}</p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* Interviews */}
        <Section title={t("recruitment.analytics.interviewsTitle")}>
          <div className="grid grid-cols-2 gap-3">
            <div
              className="rounded-xl p-4 text-center"
              style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
            >
              <p className="text-2xl font-semibold" style={{ color: "#5e6ad2", letterSpacing: "-0.5px" }}>
                {stats.interviews_total}
              </p>
              <p className="text-xs mt-1" style={{ color: "var(--lm-ink-3)" }}>{t("recruitment.analytics.totalScheduled")}</p>
            </div>
            <div
              className="rounded-xl p-4 text-center"
              style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
            >
              <p className="text-2xl font-semibold" style={{ color: "#16a34a", letterSpacing: "-0.5px" }}>
                {stats.interviews_completed}
              </p>
              <p className="text-xs mt-1" style={{ color: "var(--lm-ink-3)" }}>{t("recruitment.analytics.completed")}</p>
            </div>
          </div>
          {stats.interviews_total > 0 && (
            <div className="mt-4 space-y-1">
              <div className="flex justify-between text-xs">
                <span style={{ color: "var(--lm-ink-3)" }}>{t("recruitment.analytics.completionRate")}</span>
                <span style={{ color: "var(--lm-ink-2)" }}>
                  {Math.round((stats.interviews_completed / stats.interviews_total) * 100)}%
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--lm-s2)" }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.round((stats.interviews_completed / stats.interviews_total) * 100)}%`,
                    backgroundColor: "#16a34a",
                  }}
                />
              </div>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color }) {
  return (
    <div
      className="rounded-xl p-4 space-y-3"
      style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}18`, border: `1px solid ${color}30` }}
      >
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-semibold" style={{ color: "var(--lm-ink)", letterSpacing: "-0.5px" }}>{value}</p>
        <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{label}</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div
      className="rounded-xl p-5 space-y-4"
      style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
    >
      <h4 className="text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>{title}</h4>
      {children}
    </div>
  );
}
